import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer, staggerItem } from '../../animations/animations';
import { SiC, SiPython, SiGnubash, SiLinux, SiDocker, SiGitlab, SiGit, SiArduino } from 'react-icons/si';
import {
  MdCode, MdMemory, MdDeveloperBoard, MdTerminal, MdBuild, MdApps, MdSettings,
  MdBugReport, MdSearch, MdWifi, MdUsb, MdDeviceHub,
  MdLock, MdVerifiedUser, MdSecurity, MdSystemUpdate,
  MdGroups, MdDescription,
} from 'react-icons/md';

const skillCategories = [
  {
    title: 'Languages',
    skills: [
      { name: 'C',            icon: MdCode    },
      { name: 'Embedded C',   icon: SiC       },
      { name: 'Python',       icon: SiPython  },
      { name: 'Bash / Shell', icon: SiGnubash },
    ],
  },
  {
    title: 'Embedded & OS',
    skills: [
      { name: 'Linux (User Space & Kernel)', icon: SiLinux         },
      { name: 'FreeRTOS',                    icon: MdMemory        },
      { name: 'BSP Development',             icon: MdDeveloperBoard},
      { name: 'U-Boot',                      icon: MdSettings      },
      { name: 'Buildroot',                   icon: MdTerminal      },
      { name: 'crosstool-ng',                icon: MdBuild         },
      { name: 'CMake',                       icon: MdBuild         },
      { name: 'Linux Namespaces',            icon: MdApps          },
      { name: 'systemd / initd',             icon: MdSettings      },
    ],
  },
  {
    title: 'Debugging & Tools',
    skills: [
      { name: 'GDB',            icon: MdBugReport     },
      { name: 'strace',         icon: MdSearch        },
      { name: 'Valgrind',       icon: MdBugReport     },
      { name: 'Ghidra',         icon: MdSearch        },
      { name: 'Wireshark',      icon: MdWifi          },
      { name: 'Logic Analyzer', icon: MdDeveloperBoard},
      { name: 'Git',            icon: SiGit           },
      { name: 'GitLab CI',      icon: SiGitlab        },
      { name: 'Docker',         icon: SiDocker        },
    ],
  },
  {
    title: 'Hardware & SoCs',
    skills: [
      { name: 'ARM Cortex-A/M',  icon: MdMemory        },
      { name: 'AMLOGIC',         icon: MdDeveloperBoard},
      { name: 'CVITEK',          icon: MdDeveloperBoard},
      { name: 'ESP32 / ESP32-S3',icon: SiArduino       },
      { name: 'STM32',           icon: MdDeveloperBoard},
      { name: 'Custom ASICs',    icon: MdMemory        },
    ],
  },
  {
    title: 'Protocols',
    skills: [
      { name: 'I2C (incl. bit-banging)', icon: MdDeviceHub },
      { name: 'UART',                    icon: MdDeviceHub },
      { name: 'USB',                     icon: MdUsb       },
      { name: 'TCP / IP',                icon: MdWifi      },
    ],
  },
  {
    title: 'Security',
    skills: [
      { name: 'AES-256-CBC',    icon: MdLock         },
      { name: 'RSA-2048',       icon: MdLock         },
      { name: 'SHA-256',        icon: MdVerifiedUser },
      { name: 'OpenSSL',        icon: MdSecurity     },
      { name: 'Secure OTA',     icon: MdSystemUpdate },
      { name: 'Firmware Signing',icon: MdVerifiedUser},
    ],
  },
  {
    title: 'Competencies',
    skills: [
      { name: 'Board Bring-up',               icon: MdDeveloperBoard},
      { name: 'Hardware-Software Integration', icon: MdSettings      },
      { name: 'Reverse Engineering',           icon: MdSearch        },
      { name: 'Technical Documentation',       icon: MdDescription   },
      { name: 'Agile / Scrum',                 icon: MdGroups        },
    ],
  },
];

const SkillCategory = ({ category, index }) => {
  return (
    <motion.div
      variants={staggerItem}
      className="card"
      data-aos="fade-up"
      data-aos-delay={index * 100}
    >
      <h3 className="text-xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
        {category.title}
      </h3>
      <div className="flex flex-wrap gap-3">
        {category.skills.map((skill, idx) => (
          <motion.div
            key={idx}
            whileHover={{ scale: 1.05, y: -2 }}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 hover:border-primary/50 hover:bg-primary/10 transition-all cursor-default"
          >
            <skill.icon className="w-5 h-5 text-primaryLight" />
            <span className="text-textPrimary text-sm font-medium">{skill.name}</span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

const Skills = () => {
  return (
    <section id="skills" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.h2
          variants={fadeInUp}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="section-header"
        >
          Technical Skills
        </motion.h2>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {skillCategories.map((category, index) => (
            <SkillCategory key={index} category={category} index={index} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
